import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [recoveryToken, setRecoveryToken] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // Validate recovery token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        // Check for query parameters (new format: ?token=XXX&type=recovery)
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams?.get('token');
        const type = queryParams?.get('type');
        
        // Also check hash parameters for backward compatibility
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const accessToken = hashParams?.get('access_token');
        const refreshToken = hashParams?.get('refresh_token');
        const hashType = hashParams?.get('type');
        
        // New format: query parameters with token
        if (type === 'recovery' && token) {
          console.log('Recovery token found in query params');
          setRecoveryToken(token);
          setTokenValid(true);
          setValidatingToken(false);
          return;
        }
        
        // Old format: hash parameters with access_token (backward compatibility)
        if (hashType === 'recovery' && accessToken) {
          console.log('Recovery token found in hash params (legacy)');
          // Set the session using the recovery tokens
          const { data: sessionData, error: sessionError } = await supabase?.auth?.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (sessionError || !sessionData?.session) {
            console.error('Session error:', sessionError);
            setError('Lien de réinitialisation invalide ou expiré');
            setTokenValid(false);
          } else {
            setRecoveryToken('legacy');
            setTokenValid(true);
          }
          setValidatingToken(false);
          return;
        }
        
        // No valid token found
        setError('Lien de réinitialisation invalide ou expiré');
        setTokenValid(false);
        setValidatingToken(false);
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Erreur lors de la validation du lien');
        setTokenValid(false);
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [location]);

  // Password strength validation
  useEffect(() => {
    setPasswordStrength({
      hasMinLength: newPassword?.length >= 8,
      hasUpperCase: /[A-Z]/?.test(newPassword),
      hasLowerCase: /[a-z]/?.test(newPassword),
      hasNumber: /[0-9]/?.test(newPassword),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/?.test(newPassword)
    });
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validation
    if (!newPassword || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (!passwordStrength?.hasMinLength) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (!passwordStrength?.hasUpperCase || !passwordStrength?.hasLowerCase) {
      setError('Le mot de passe doit contenir des majuscules et des minuscules');
      return;
    }

    if (!passwordStrength?.hasNumber) {
      setError('Le mot de passe doit contenir au moins un chiffre');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // New format: verify OTP and update password
      if (recoveryToken && recoveryToken !== 'legacy') {
        console.log('Using verifyOtp for password reset');
        
        // Verify the OTP token and get session
        const { data: verifyData, error: verifyError } = await supabase?.auth?.verifyOtp({
          token_hash: recoveryToken,
          type: 'recovery'
        });
        
        if (verifyError) {
          console.error('OTP verification error:', verifyError);
          setError('Lien de réinitialisation invalide ou expiré');
          setLoading(false);
          return;
        }
        
        console.log('OTP verified successfully, updating password');
        
        // Update password using the verified session
        const { error: updateError } = await supabase?.auth?.updateUser({
          password: newPassword
        });
        
        if (updateError) {
          console.error('Password update error:', updateError);
          setError('Erreur lors de la mise à jour du mot de passe');
          setLoading(false);
          return;
        }
      } else {
        // Legacy format: session already set, just update password
        console.log('Using legacy session for password reset');
        const { error: updateError } = await supabase?.auth?.updateUser({
          password: newPassword
        });
        
        if (updateError) {
          console.error('Password update error:', updateError);
          setError('Erreur lors de la mise à jour du mot de passe');
          setLoading(false);
          return;
        }
      }

      setSuccess(true);
      
      // Sign out to clear the recovery session
      await supabase?.auth?.signOut();
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err) {
      console.error('Password reset error:', err);
      if (err?.message?.includes('session')) {
        setError('Session expirée. Veuillez demander un nouveau lien de réinitialisation.');
      } else {
        setError('Erreur lors de la réinitialisation du mot de passe. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (isValid) => {
    return isValid ? 'text-green-400' : 'text-gray-500';
  };

  const getStrengthIcon = (isValid) => {
    return isValid ? 'CheckCircle' : 'Circle';
  };

  const handleReturnToLogin = async () => {
    // Clear any existing session before redirecting
    try {
      await supabase?.auth?.signOut();
    } catch (error) {
      // Ignore errors, just redirect
    }
    navigate('/login', { replace: true });
  };

  // Loading state
  if (validatingToken) {
    return (
      <>
        <Helmet>
          <title>Réinitialisation du mot de passe - EndoReferral</title>
        </Helmet>
        <div className="min-h-screen bg-[#1A1A1E] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-300">Validation du lien...</p>
          </div>
        </div>
      </>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <>
        <Helmet>
          <title>Lien invalide - EndoReferral</title>
        </Helmet>
        <div className="min-h-screen bg-[#1A1A1E] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-[#1A1A1E] to-[#1A1A1E]" />
          
          <div className="w-full max-w-md relative z-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl mb-4 shadow-2xl">
                <Icon name="AlertCircle" size={36} className="text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Lien invalide
              </h1>
              <p className="text-gray-400 mb-6">
                {error || 'Ce lien de réinitialisation est invalide ou a expiré'}
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border border-white/10 p-8">
              <div className="space-y-4">
                <div className="bg-yellow-600/20 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-sm text-yellow-300">
                    Les liens de réinitialisation expirent après 1 heure pour des raisons de sécurité.
                  </p>
                </div>

                <Button
                  onClick={handleReturnToLogin}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-medium transition-all shadow-xl flex items-center justify-center gap-2"
                >
                  <Icon name="ArrowLeft" size={20} />
                  Retour à la connexion
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Success state
  if (success) {
    return (
      <>
        <Helmet>
          <title>Mot de passe mis à jour - EndoReferral</title>
        </Helmet>
        <div className="min-h-screen bg-[#1A1A1E] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-[#1A1A1E] to-[#1A1A1E]" />
          
          <div className="w-full max-w-md relative z-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-600 to-green-700 rounded-2xl mb-4 shadow-2xl">
                <Icon name="CheckCircle" size={36} className="text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Mot de passe mis à jour
              </h1>
              <p className="text-gray-400">
                Votre mot de passe a été réinitialisé avec succès
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border border-white/10 p-8">
              <div className="bg-green-600/20 border border-green-500/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-300 text-center">
                  Redirection vers la page de connexion...
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Password reset form
  return (
    <>
      <Helmet>
        <title>Réinitialiser votre mot de passe - EndoReferral</title>
      </Helmet>
      <div className="min-h-screen bg-[#1A1A1E] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-[#1A1A1E] to-[#1A1A1E]" />
        
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl mb-4 shadow-2xl">
              <Icon name="Lock" size={36} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Réinitialiser votre mot de passe
            </h1>
            <p className="text-gray-400">
              Créez un nouveau mot de passe sécurisé pour votre compte EndoReferral
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border border-white/10 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e?.target?.value);
                      if (error) setError('');
                    }}
                    placeholder="••••••••"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500 pr-12"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                    disabled={loading}
                  >
                    <Icon name={showNewPassword ? 'EyeOff' : 'Eye'} size={20} />
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e?.target?.value);
                      if (error) setError('');
                    }}
                    placeholder="••••••••"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500 pr-12"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                    disabled={loading}
                  >
                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
                  </button>
                </div>
              </div>

              {/* Password strength indicators */}
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-300 mb-3">Critères du mot de passe :</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={getStrengthIcon(passwordStrength?.hasMinLength)} 
                      size={16} 
                      className={getStrengthColor(passwordStrength?.hasMinLength)}
                    />
                    <span className={`text-sm ${getStrengthColor(passwordStrength?.hasMinLength)}`}>
                      Au moins 8 caractères
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={getStrengthIcon(passwordStrength?.hasUpperCase && passwordStrength?.hasLowerCase)} 
                      size={16} 
                      className={getStrengthColor(passwordStrength?.hasUpperCase && passwordStrength?.hasLowerCase)}
                    />
                    <span className={`text-sm ${getStrengthColor(passwordStrength?.hasUpperCase && passwordStrength?.hasLowerCase)}`}>
                      Majuscules et minuscules
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={getStrengthIcon(passwordStrength?.hasNumber)} 
                      size={16} 
                      className={getStrengthColor(passwordStrength?.hasNumber)}
                    />
                    <span className={`text-sm ${getStrengthColor(passwordStrength?.hasNumber)}`}>
                      Au moins un chiffre
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={getStrengthIcon(passwordStrength?.hasSpecialChar)} 
                      size={16} 
                      className={getStrengthColor(passwordStrength?.hasSpecialChar)}
                    />
                    <span className={`text-sm ${getStrengthColor(passwordStrength?.hasSpecialChar)}`}>
                      Caractère spécial (recommandé)
                    </span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-medium transition-all shadow-xl flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Réinitialisation...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Lock" size={20} />
                    <span>Réinitialiser le mot de passe</span>
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleReturnToLogin}
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors inline-flex items-center gap-1"
                  disabled={loading}
                >
                  <Icon name="ArrowLeft" size={16} />
                  Retour à la connexion
                </button>
              </div>
            </form>
          </div>

          {/* Security notice */}
          <div className="mt-6 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 p-4">
            <div className="flex items-start gap-3">
              <Icon name="Shield" size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-300 font-medium mb-1">Sécurité</p>
                <p className="text-xs text-gray-400">
                  Ce lien de réinitialisation expire après 1 heure et ne peut être utilisé qu'une seule fois.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PasswordReset;